<template>
  <aha-modal
    :closable="false"
    :width="'460px'"
    :blurBackdrop="true"
  >
    <main role="content" class="popup__content-main">
      <div class="slide-wrapper">
        <div class="welcome-slide-1__content">
          <div class="text-center welcome-slide-1__content-heading">
            <h2 class="mb-2 font-bold md:text-xl">
              Where do you plan to use AhaSlides?
            </h2>
          </div>
          <div
            v-for="(option, optionIndex) in onboardingOptions"
            :key="optionIndex"
            class="onboarding-options__wrapper"
            @click="handleUpdateUserPersonalInfo(option)"
          >
            <div
              class="onboarding-options__content"
              :class="option.customClass"
            >
              <p class="z-20 pl-5 md:text-lg">
                {{ option.name }}
              </p>
              <img class="z-10 onboarding-options__content-image" :src="option.optionImage">
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-center mb-4">
        <a class="text-sm cursor-pointer text-base-70" @click="onSkipWelcomePopup">
          Skip
        </a>
      </div>
    </main>
  </aha-modal>
</template>

<script>
// services
import Escapable from '@mindthegapstudio/stpancras-storybook-app/src/components/mixins/Escapable.vue'
import { mapState, mapActions } from 'vuex'
import cookie from '@/services/cookie'

// api
import UserApi from '@/api/user.js'
import { actions as userActions } from '@/api/user'

// components
import constants from '@/constant'
import utils from '@/util'

// modules
import offlineWatcher from '@/modules/offlineWatcher'

const { gaCategories } = constants

export default {
  mixins: [Escapable],
  data() {
    return {
      escapableVisible: true,
      onboardingOptions: constants.onboardingOptions,
      optionSelected: {},
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      isComeFromSignupPage: (state) => state.isComeFromSignupPage,
    }),
  },
  created() {
    this.preloadOnboardingImages()
  },
  mounted() {
    utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Welcome Popup > Open popup')
  },
  methods: {
    ...mapActions(['setCurrentUserAction']),
    preloadOnboardingImages() {
      utils.preloadOnboardingImages()
    },

    onSkipWelcomePopup() {
      utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Welcome Popup > Skip popup')
      localStorage.setItem(`skipped-welcome-popup-${this.currentUser.id}`, 'true')
      this.closePopup()
    },

    closePopup() {
      this.$emit('closePopup')
      utils.gaTrackEvent(gaCategories.PRESENTATION_LIST, 'Welcome Popup > I\'m ready > Close popup')
    },
    setFlagUserUpdatedGA4(user) {
      utils.ga4Set('user_properties', {
        first_page: user.firstPage,
        referral_user_id: user.referralUserId,
        sign_up_source: user.signUpSource,
        use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor
          : null,
        updated_at: user.updatedAt,
      })
    },
    async handleUpdateUserPersonalInfo(option) {
      this.optionSelected = option
      this._handleGaTrackingFirstSlide()

      const payload = {
        personalInformation: {
          useAhaSlidesFor: option.useAhaSlidesFor,
        },
      }

      try {
        await UserApi.patchUserById(this.currentUser.id, payload)
      } catch (error) {
        offlineWatcher.addTask({
          action: userActions.patchUserById,
          params: { userId: this.currentUser.id },
          payload,
        })
      }
      cookie.deleteValue('aha-new-user')

      this.closePopup()

      // avoid losing existing personalInformation
      payload.personalInformation = {
        ...this.currentUser.personalInformation,
        ...payload.personalInformation,
      }
      // set currentUser so slide 2 can get info even if update fails
      this.setCurrentUserAction({
        ...this.currentUser,
        ...payload,
      })
      this.setFlagUserUpdatedGA4(this.currentUser)
    },

    _handleGaTrackingFirstSlide() {
      utils.gaTrackEvent(
        constants.gaCategories.PRESENTATION_LIST,
        'Welcome Popup > Slide 1 > Select option',
        this.optionSelected.name,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.slide-wrapper {
  @apply px-2 pb-2;
}

.popup__wrapper {
  @apply fixed inset-0 flex items-center justify-center mx-auto;
  z-index: 10000000000;
}

.popup__content {
  @apply z-10 overflow-y-auto bg-base-0;
  width: 320px;
  height: 320px;
  -webkit-overflow-scrolling: touch;
  .popup__content-main {
    @apply px-6 md:pt-1 bg-base-0;
    min-height: 20rem;
    .lottie-img {
      max-height: 710px;
    }
  }
}

.onboarding-options__wrapper {
  @apply flex items-center justify-center w-full;
  @apply text-sm md:text-base text-base-0 font-bold;
  @apply cursor-pointer relative mb-6;

  .onboarding-options__content {
    @apply flex items-center justify-between h-12 md:h-18 relative;
    border-radius: 8px;
    width: 360px;
    background-color: #9378E8;
    box-shadow: 0px 6px 0px #7B55CC;

    &:active:not(:disabled) {
      @apply transform translate-y-1.5 shadow-none;
    }

    &:hover:not(:active):not(:disabled) {
      @apply transform translate-y-0.25;
    }

    .onboarding-options__content-image {
      @apply h-full absolute right-0 top-0;
      opacity: 0.4;
      border-radius: 8px;
    }

    &:hover {
      box-shadow: 0px 5px 0px #7B55CC;
    }

    &.work {
      background-color: #008DFE;
      box-shadow: 0px 6px 0px #006CDC;

      &:hover {
        box-shadow: 0px 5px 0px #006CDC;
      }

      &:active {
        box-shadow: 0px 0px 0px #006CDC;
      }
    }

    &.community {
      background-color: #33C173;
      box-shadow: 0px 6px 0px #0FA656;

      &:hover {
        box-shadow: 0px 5px 0px #0FA656;
      }

      &:active {
        box-shadow: 0px 0px 0px #0FA656;
      }
    }

    &.others {
      background-color: #525F7F;
      box-shadow: 0px 6px 0px #3C4968;

      &:hover {
        box-shadow: 0px 5px 0px #3C4968;
      }

      &:active {
        box-shadow: 0px 0px 0px #3C4968;
      }
    }
  }
}

.onboarding-options__image {
  @apply h-12 bg-base-10 rounded-full mr-4;
  min-width: 64px;
  min-height: 64px;
}

.onboarding-benefits__wrapper {
  @apply pb-5 pt-7 md:w-5/6 w-full;
}

.onboarding-benefit__content {
  @apply flex items-center justify-start w-full;
  @apply py-2 mb-5;
  @apply text-base;
  min-height: 5rem;
}

.welcome-slide-1__content, .welcome-slide-2__content {
  @apply flex flex-col items-center my-0 mx-auto;
}

.welcome-slide-1__content {
  max-width: 30rem;
  .welcome-slide-1__content-heading {
    @apply text-center mb-7;
  }
}

.welcome-slide-2__content-wrapper {
  @apply my-0 mx-auto;
  max-width: 35rem;
}

.welcome-slide-2__content-wrapper {
  @apply bg-no-repeat bg-contain;
}
.popup__footer {
  @apply my-0 w-1/2;
  @apply rounded-b-none md:relative md:rounded-b-2xl bg-base-0;

  @media (max-width: 600px) {
    width: 100%;
    max-width: 400px;
  }
}

.popup__step {
  color: #868686;
  @apply text-center uppercase text-xs pb-1 md:pb-0;
}
</style>
